import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Twitter, Share2, Check, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const WinShareCard = ({ pick, onClose }) => {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Calculate profit from the pick
  const profit = pick.result === 'win' ? pick.bet_amount : -pick.bet_amount;
  const profitDisplay = profit >= 0 ? `+$${profit.toLocaleString()}` : `-$${Math.abs(profit).toLocaleString()}`;

  // Parse matchup for display
  const teams = pick.matchup.split(/\s+(?:@|vs\.?|at)\s+/i);
  const team1 = teams[0] || pick.matchup;
  const team2 = teams[1] || '';

  // Format date in Eastern Time
  const gameDate = new Date(pick.game_time).toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const handleDownload = async () => {
    if (!cardRef.current) return;
    
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#0a0a0a'
      });
      
      const link = document.createElement('a');
      link.download = `hsl-win-${pick.id.slice(0, 8)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('Image downloaded!');
    } catch (error) {
      toast.error('Failed to generate image');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyImage = async () => {
    if (!cardRef.current) return;
    
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#0a0a0a'
      });
      
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      
      setCopied(true);
      toast.success('Image copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy image');
      console.error(error);
    }
  };

  const handleShareTwitter = async () => {
    const text = `🔨 HAMMER HITS! ${pick.pick} ✅\n\n${profitDisplay} profit\n\nTrack our Road to $100K at hsl.bet\n\n#SportsBetting #GamblingTwitter #HSLBet`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-hsl-surface border border-white/10 rounded-2xl max-w-lg w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h3 className="font-heading text-lg font-bold uppercase text-white">Share Your Win</h3>
            <button onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* The Shareable Card */}
          <div className="p-4">
            <div
              ref={cardRef}
              className="relative bg-gradient-to-br from-[#0a0a0a] via-[#111111] to-[#0a0a0a] rounded-xl overflow-hidden"
              style={{ width: '100%', aspectRatio: '1.91/1' }}
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)'
                }} />
              </div>

              {/* Glow Effect */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-green-500/20 rounded-full blur-3xl" />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-6">
                {/* Top Row - Logo & Result */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-black text-lg">H</span>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">HSL.BET</p>
                      <p className="text-zinc-500 text-xs">Hammer Sports Logic</p>
                    </div>
                  </div>
                  <div className="bg-green-500/20 border border-green-500/30 rounded-full px-3 py-1 flex items-center gap-1">
                    <Check className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 font-bold text-sm uppercase">Winner</span>
                  </div>
                </div>

                {/* Middle - Pick Details */}
                <div className="text-center py-4">
                  <p className="text-zinc-400 text-sm mb-1">{pick.sport} • {gameDate}</p>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-white uppercase tracking-tight mb-2">
                    {team1} {team2 ? `@ ${team2}` : ''}
                  </h2>
                  <div className="inline-block bg-white/5 border border-white/10 rounded-lg px-4 py-2">
                    <p className="text-cyan-400 font-bold text-lg">{pick.pick}</p>
                    <p className="text-zinc-500 text-sm">{pick.odds}</p>
                  </div>
                </div>

                {/* Bottom Row - Profit */}
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-zinc-500 text-xs uppercase tracking-wide">Profit</p>
                    <p className="text-green-400 font-mono text-2xl font-bold">{profitDisplay}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-zinc-500 text-xs uppercase tracking-wide">Road to $100K</p>
                    <div className="flex items-center gap-1 text-cyan-400">
                      <TrendingUp className="w-4 h-4" />
                      <span className="font-bold text-sm">Follow the journey</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Border Glow */}
              <div className="absolute inset-0 rounded-xl border border-green-500/20 pointer-events-none" />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-t border-white/10 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/10"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading ? 'Generating...' : 'Download'}
              </Button>
              <Button
                onClick={handleCopyImage}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/10"
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Image'}
              </Button>
            </div>
            <Button
              onClick={handleShareTwitter}
              className="w-full bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
            >
              <Twitter className="w-4 h-4 mr-2" />
              Share on X (Twitter)
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WinShareCard;
